import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Col, Form, Row, Typography } from 'antd';

import PostalCodeInput from '@/views/components/Selectors/PostalCodeInput';
import { CountrySelect, StateSelect } from '@/views/screens/common';

import { getCarrierSearchProgress } from '../../../../redux/common/CommonActions';
import Button from '../../../components/Buttons/Button';
import FormInput from '../../../components/Inputs/FormInput';

const hasSearchCriteria = (values = {}) =>
  ['CarrierName', 'CarrierScacCode', 'postalCode'].some(
    (field) => typeof values[field] === 'string' && values[field].trim().length > 0,
  );

const CarrierSearchForm = ({ formInstance, onSubmit, labelForTestId, modalTypeForTest, initialValues = {} }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['translation']);

  let [form] = Form.useForm();
  if (formInstance) {
    form = formInstance;
  }

  const [showCriteriaError, setShowCriteriaError] = useState(false);

  const countryCode = Form.useWatch('countryCode', form);
  const handleSubmit = (data) => {
    if (!hasSearchCriteria(data)) {
      setShowCriteriaError(true);
      return;
    }

    setShowCriteriaError(false);

    const payload = {
      CarrierName: data?.CarrierName,
      CarrierScacCode: data?.CarrierScacCode,
      CarrierZip: data?.postalCode,
      CountryCode: data?.countryCode,
      PageIndex: 1,
      PageSize: 10,
    };
    dispatch(getCarrierSearchProgress(payload));
    if (onSubmit) {
      onSubmit(data);
    }
  };

  const handleZipDetails = (data) => {
    form.setFieldsValue({
      city: data?.city,
      countryCode: data?.countryCode,
      postalCode: data?.zip,
      stateCode: data?.state,
    });
  };

  const handleValuesChange = (_, allValues) => {
    if (hasSearchCriteria(allValues)) {
      setShowCriteriaError(false);
    }
  };

  return (
    <Form form={form} onFinish={handleSubmit} initialValues={initialValues || {}} onValuesChange={handleValuesChange}>
      <Row gutter={15}>
        <Col md={8}>
          <FormInput
            name="CarrierName"
            label={t('translation:default.fields.companyName.label')}
            placeholder={t('translation:default.fields.companyName.placeholder')}
            editMode
          />
        </Col>
        <Col md={8}>
          <FormInput
            name="CarrierScacCode"
            label={t('translation:default.fields.scac.label')}
            placeholder={t('translation:default.fields.scac.placeholder')}
            editMode
          />
        </Col>
        <Col md={8}>
          <CountrySelect name="countryCode" />
        </Col>
        <Col md={8}>
          <PostalCodeInput
            name="postalCode"
            label={t('translation:default.fields.postalCode.label')}
            placeholder={t('translation:default.fields.postalCode.placeholder')}
            editMode
            handleZipDetails={handleZipDetails}
            countryCode={countryCode}
            labelForTestId={labelForTestId}
          />
        </Col>
        <Col md={8}>
          <StateSelect editMode={false} name="stateCode" countryCode={countryCode} />
        </Col>
        <Col md={8}>
          <FormInput label={t('translation:default.fields.city.label')} editMode={false} name="city" />
        </Col>
      </Row>
      {showCriteriaError && (
        <Row>
          <Col span={24}>
            <Typography.Text type="danger" role="alert">
              At least one value for Name, Postal Code, or SCAC is required.
            </Typography.Text>
          </Col>
        </Row>
      )}
      <Row justify="end" gutter={15}>
        <Col>
          <Button buttonType="submit" data-testid={`${labelForTestId}-${modalTypeForTest}-search`}>
            {t('translation:default.buttons.search')}
          </Button>
        </Col>
        <Col>
          <Button
            onClick={() => {
              setShowCriteriaError(false);
              form.resetFields();
            }}
            type="default"
            data-testid={`${labelForTestId}-${modalTypeForTest}-clear`}
          >
            {t('translation:default.buttons.clear')}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default CarrierSearchForm;
